import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { Loader } from "../loader/Loader";
import { Avatar } from "../Avatar";
import { FullModal } from "../../modules/world/rmc/modals/fullModal";
import { UserContext } from "../context/UseContext";
import { ProfileBadge } from "../world/rmc/ProfileBadge";
import ProgressBar from "../world/rmc/ProgressBar";
import Button from "../world/rmc/Button";

const scoreBreakdown = (rewards = {}) => {
  const data = rewards?.data || {};
  return [
    {
      id: "quiz-score",
      label: "Quiz Score",
      points: Number(data?.score) || 0,
      type: "primary",
    },
    {
      id: "high-score-bonus",
      label: "High Score Bonus",
      points: data?.high_score_bonus,
      type: "bonus",
    },
    {
      id: "daily-play-bonus",
      label: "Daily Play Bonus",
      points: data?.daily_bonus,
      type: "bonus",
    },
  ].filter((item) => item.points !== null && item.points !== undefined);
};

function WordTriviaGameOverPrompt({
  rewards,
  isLoading,
  apiError,
  onPlayAgain,
  onExit,
  gameStatus,
  percentCompleted,
}) {
  const navigate = useNavigate();
  const { profile } = useContext(UserContext);

  const name = `${profile?.first_name} ${profile?.last_name}`;
  const scoreData = scoreBreakdown(rewards);

  // Calculate badge progress from the API response
  const getBadgeProgress = () => {
    if (!rewards?.data) return null;

    const currentBadge = rewards.data.current_badge;
    const nextBadge = rewards.data.next_badge;
    const audiencePoints = rewards.data.audience_point;

    if (!currentBadge || !nextBadge || audiencePoints === undefined)
      return null;

    return {
      currentPoints: audiencePoints,
      currentBadge,
      nextBadge,
    };
  };

  const badgeProgress = getBadgeProgress();

  const getGameMessage = () => {
    if (percentCompleted === 100) {
      return "Congratulations! You found all words!";
    }
    return "Time's up! Better luck next time!";
  };

  const ReturnIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11.896 18C11.896 18.1989 11.817 18.3897 11.6763 18.5303C11.5357 18.671 11.3449 18.75 11.146 18.75C7.354 18.75 4.25 15.745 4.25 12C4.25 8.255 7.354 5.25 11.146 5.25C14.251 5.25 16.895 7.265 17.751 10.051L18.354 9.031C18.4552 8.85967 18.6203 8.73555 18.813 8.68595C19.0057 8.63634 19.2102 8.66532 19.3815 8.7665C19.5528 8.86768 19.6769 9.03278 19.7266 9.22547C19.7762 9.41816 19.7472 9.62267 19.646 9.794L18.016 12.549C17.9162 12.718 17.7541 12.8411 17.5645 12.892C17.375 12.9429 17.173 12.9174 17.002 12.821L14.18 11.23C14.0942 11.1816 14.0187 11.1168 13.958 11.0392C13.8972 10.9617 13.8523 10.8729 13.8258 10.778C13.7994 10.6831 13.7919 10.5839 13.8037 10.4861C13.8156 10.3883 13.8466 10.2938 13.895 10.208C13.9434 10.1222 14.0082 10.0467 14.0858 9.98597C14.1633 9.9252 14.2521 9.8803 14.347 9.85384C14.4419 9.82737 14.5411 9.81986 14.6389 9.83173C14.7367 9.84359 14.8312 9.87461 14.917 9.923L16.389 10.753C15.815 8.465 13.698 6.75 11.147 6.75C8.149 6.75 5.75 9.117 5.75 12C5.75 14.883 8.149 17.25 11.146 17.25C11.3449 17.25 11.5357 17.329 11.6763 17.4697C11.817 17.6103 11.896 17.8011 11.896 18Z"
        fill="currentColor"
      />
    </svg>
  );

  const CancelIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M19.7091 5.71002L18.2891 4.29004L11.9991 10.59L5.70905 4.29004L4.28906 5.71002L10.5891 12L4.28906 18.2901L5.70905 19.71L11.9991 13.41L18.2891 19.71L19.7091 18.2901L13.4091 12L19.7091 5.71002Z"
        fill="currentColor"
      />
    </svg>
  );

  return (
    <FullModal className="fixed inset-0 z-[4000] bg-[#111522] animate-slide-up 480:max-w-[440px] 480:mx-auto overflow-y-auto hidden-scrollbar">
      <div className="px-4 pt-16 relative flex flex-col h-screen">
        <div className="flex-1">
          <p className="text-xl font-semibold text-center text-white mb-10">
            Game Over
          </p>

          <div className="grid justify-items-center px-4">
            {/* Avatar */}
            {profile?.profile_image ? (
              <ProfileBadge
                image={profile?.profile_image}
                className="w-24 h-24 p-[2px]"
                gradientClass={"border-gradient"}
              />
            ) : (
              <Avatar
                name={name}
                size="xl"
                customBgColor={"#2741d6"}
                customTextColor={"#fff"}
              />
            )}

            <div className="mt-3 mb-2">
              {isLoading ? (
                <>
                  <div className="flex items-center justify-center py-8">
                    <Loader type="spinningBubbles" color="#2741d6" />
                  </div>
                </>
              ) : rewards?.data ? (
                <>
                  <h3 className="text-2xl font-bold text-center text-white mb-2">
                    {getGameMessage()}
                  </h3>
                  <p className="text-xl font-semibold text-center text-white mb-1">
                    {rewards?.data?.total_points || 0} pts
                  </p>
                  <div className="bg-blackhue-rmc_hue py-2 px-4 rounded-[120px] text-center">
                    <p className="text-[#FFFFFFBF] text-xs text-center font-medium">
                      Points earned for this round
                    </p>
                  </div>
                </>
              ) : apiError ? (
                <>
                  <h3 className="text-2xl font-bold text-center text-white mb-2">
                    {getGameMessage()}
                  </h3>
                  <div className="bg-blackhue-rmc_hue py-2 px-4 rounded-[120px] text-center">
                    <p className="text-[#FFFFFFBF] text-xs text-center font-medium">
                      Game completed but unable to sync rewards. Please try
                      again.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="text-2xl font-bold text-center text-white mb-2">
                    {getGameMessage()}
                  </h3>
                  <div className="bg-blackhue-rmc_hue py-2 px-4 rounded-[120px] text-center">
                    <p className="text-[#FFFFFFBF] text-xs text-center font-medium">
                      Points earned for this round
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Progress Bar */}
            <div className="w-full mb-8">
              {!isLoading && rewards?.data && badgeProgress && (
                <ProgressBar
                  currPoints={badgeProgress.currentPoints}
                  totalPoints={badgeProgress.nextBadge.points}
                  currentlevel={badgeProgress.currentBadge.name}
                  nextlevel={badgeProgress.nextBadge.name}
                  hideNextPoints={!rewards?.data?.next_badge}
                  strokeColor={"#2741d6"}
                  centerLabel={`${badgeProgress.currentPoints || 0} pts`}
                />
              )}
            </div>

            {/* Score Breakdown */}
            {!isLoading && rewards?.data && scoreData.length > 0 && (
              <div className="flex flex-col gap-4 w-full my-10">
                {scoreData.map((x, i) => {
                  return (
                    <div
                      key={i}
                      className="border border-[#252B41] rounded-md overflow-hidden flex items-center "
                    >
                      <div className="flex-1 bg-blackhue-rmc_hue p-4 text-base font-semibold text-[#FFFFFFBF]">
                        {x.label}
                      </div>
                      <div className="bg-transparent flex-1 p-4 pe-8 text-end text-base font-semibold text-[#FFFFFFBF]">
                        {x.points}pts
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between items-center mt-10 left-5 right-5 480:left-auto 480:right-auto mb-20">
            <button
              onClick={onPlayAgain}
              disabled={isLoading}
              className={clsx(
                "py-3 px-5 flex items-center gap-2 rounded-lg font-medium text-base transition-colors",
                percentCompleted === 100
                  ? "bg-[#2741d6] text-white hover:bg-[#2741d6]/90"
                  : "bg-[#EF4444] text-white hover:bg-[#EF4444]/90",
              )}
            >
              <ReturnIcon />
              {percentCompleted === 100 ? "Play Again" : "Retry"}
            </button>

            <button
              onClick={onExit}
              disabled={isLoading}
              className="px-5 py-3 flex items-center gap-2 rounded-lg font-medium text-base transition-colors bg-[#1A1F33] text-white"
            >
              <CancelIcon />
              Exit Game
            </button>
          </div>
        </div>
        <Button
          onClick={() => {
            sessionStorage.removeItem("gameOverActive");
            navigate(
              `/campaign_id/${rewards?.data?.leaderboard?.campaign_id}?tab=leaderboard`,
            );
          }}
          text="View Leaderboard"
          className="text-lg py-[18px] mb-6 text-white !bg-[#2741d6]"
        />
      </div>
    </FullModal>
  );
}

export default WordTriviaGameOverPrompt;
